import * as React from 'react'

import { CheckIcon, History, XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { useResource } from '~/client/src/desktop/components/Resource/context/ResourceContext'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { getReferencedLocation } from '~/client/src/shared/resources/resourceLink'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import {
  readPerProjectFlag,
  writePerProjectFlag,
} from '~/client/src/shared/utils/perProjectPreference'

const PARENT_FACET_KEY = 'data.parent'
const DISMISSED_KEY = LocalStorageKey.DeletedParentLocationsBannerDismissed

interface IProps {
  projectId: string
  userId: string
  tagsStore?: TagsStore
}

// Locations left behind by the old deletion behavior: their LBS Parent is deleted, so they
// list here but not in the Maps sidebar.
function DeletedParentLocationsBanner({
  projectId,
  userId,
  tagsStore,
}: IProps) {
  const { state, actions } = useResource()
  // Read per render, not once: the page stays mounted when the project changes.
  const identity = `${userId}:${projectId}`
  const [dismissedIdentity, setDismissedIdentity] = React.useState<string>()
  const isDismissed =
    dismissedIdentity === identity ||
    !!readPerProjectFlag(userId, projectId, DISMISSED_KEY)

  if (isDismissed) return null

  const deletedParentIds = new Set<string>()
  let count = 0
  state.records.forEach(record => {
    const parentId = record.parent?.parentId
    if (parentId && getReferencedLocation(parentId, tagsStore)?.isDeleted) {
      deletedParentIds.add(parentId)
      count++
    }
  })

  if (!count) return null

  // What Review applies, still in place: only the LBS Parent filter, holding every deleted
  // parent (one whose last child was just deleted may linger), and no search.
  const parentFilter = state.appliedFilters[PARENT_FACET_KEY]
  const isReviewShown =
    !state.searchKey &&
    !!parentFilter &&
    Object.entries(state.appliedFilters).every(
      ([key, ids]) => key === PARENT_FACET_KEY || ids.size === 0,
    ) &&
    [...deletedParentIds].every(id => parentFilter.has(id)) &&
    [...parentFilter].every(
      id => !!getReferencedLocation(id, tagsStore)?.isDeleted,
    )

  const dismiss = () => {
    writePerProjectFlag(userId, projectId, DISMISSED_KEY, true)
    setDismissedIdentity(identity)
  }

  const review = () => {
    actions.setSearchKey('')
    actions.setAppliedFilters({
      [PARENT_FACET_KEY]: deletedParentIds,
    })
  }

  return (
    <div className="tw:mx-4 tw:mt-3 tw:mb-1 tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-primary/20 tw:bg-primary-soft tw:px-4 tw:py-3">
      <History className="tw:size-5 tw:shrink-0 tw:text-primary" />
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
        <span className="tw:text-title-sm tw:text-foreground">
          {Localization.translator.locationsWithDeletedParent(count)}
        </span>
        <span className="tw:text-body-md tw:text-muted-foreground">
          {Localization.translator.locationsWithDeletedParentDescription(count)}
        </span>
      </div>
      <Button onClick={review} disabled={isReviewShown}>
        {isReviewShown && <CheckIcon data-icon="inline-start" />}
        {isReviewShown
          ? Localization.translator.shownInTableBelow
          : Localization.translator.reviewLocations(count)}
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={Localization.translator.close}
        onClick={dismiss}
      >
        <XIcon />
      </Button>
    </div>
  )
}

export default inject('tagsStore')(observer(DeletedParentLocationsBanner))
